import React, { useEffect, useMemo, useState } from "react";
import { Modal, Button } from "react-bootstrap";
import { useDelegations } from "../contexts/DelegationsContext";

const moduleLabel = (moduleKey) => {
  switch (moduleKey) {
    case "onboarding":
      return "Onboarding management";
    case "conferenceScheduler":
      return "Conference scheduling";
    case "resources":
      return "Resources";
    default:
      return moduleKey;
  }
};

const moduleCapabilities = (moduleKey) => {
  switch (moduleKey) {
    case "onboarding":
      return "manage onboarding packages/documents, assign requirements, and review submissions";
    case "conferenceScheduler":
      return "manage conference sessions, attendance tools, and scheduler controls";
    case "resources":
      return "manage resources, pin/unpin posts, and update sidebar content";
    default:
      return "access delegated controls for this module";
  }
};

const LOCALSTORAGE_KEY = "dismissedDelegationIds_v1";

export default function DelegationsModal() {
  const { activeDelegations, loading } = useDelegations();
  const [show, setShow] = useState(false);

  const delegationIds = useMemo(() => activeDelegations.map((d) => d.id).filter(Boolean), [activeDelegations]);

  useEffect(() => {
    if (loading) return;
    if (!delegationIds.length) return;

    const dismissedRaw = localStorage.getItem(LOCALSTORAGE_KEY);
    const dismissedIds = dismissedRaw ? JSON.parse(dismissedRaw) : [];

    const hasUndismissed = delegationIds.some((id) => !dismissedIds.includes(id));
    if (hasUndismissed) setShow(true);
  }, [loading, delegationIds]);

  const dismiss = () => {
    try {
      const dismissedRaw = localStorage.getItem(LOCALSTORAGE_KEY);
      const dismissedIds = dismissedRaw ? JSON.parse(dismissedRaw) : [];
      const updated = Array.from(new Set([...dismissedIds, ...delegationIds]));
      localStorage.setItem(LOCALSTORAGE_KEY, JSON.stringify(updated));
    } catch (e) {
      // If localStorage is blocked, don't crash the app.
    }
    setShow(false);
  };

  if (loading) return null;

  return (
    <Modal show={show} onHide={() => setShow(false)} centered>
      <Modal.Header closeButton className="border-0 px-4 py-3">
        <div className="d-flex w-50 flex-column justify-content-start align-items-start">
          <Modal.Title>Delegated tasks active</Modal.Title>
          <div className="text-hl"></div>
        </div>
      </Modal.Header>
      <Modal.Body className="px-4">
        {activeDelegations.length === 0 ? (
          <div>No delegated tasks.</div>
        ) : (
          <div className="d-flex flex-column gap-2">
            {activeDelegations.map((d) => (
              <div key={d.id} className="d-flex flex-column border rounded p-2">
                <div className="fw-semibold">{moduleLabel(d.moduleKey)}</div>
                <div className="small mt-1">
                  Your Program Director has assigned you this task.
                </div>
                <div className="text-muted small">
                  You will be able to {moduleCapabilities(d.moduleKey)}.
                </div>
                <div className="text-muted small mt-1">
                  This delegation is active until{" "}
                  {d.endDate ? new Date(d.endDate).toLocaleDateString() : "the end date on file"}.
                </div>
              </div>
            ))}
          </div>
        )}
      </Modal.Body>
      <Modal.Footer className="px-4 border-0">
        <Button variant="secondary" size="sm" onClick={() => setShow(false)}>
          Close
        </Button>
        <Button variant="primary" size="sm" onClick={dismiss}>
          Don't show again
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

